export const ADMIN_TABS = ['orders', 'locations', 'dispatch', 'remittances', 'riders', 'customers', 'reports', 'settings', 'audit'] as const;

export type AdminTab = (typeof ADMIN_TABS)[number];

export const RIDER_APPLICATION_TABS = [
  { value: 'pending', label: 'Pending Rider Application' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'photos', label: 'Pending profile photos' },
] as const;

export type RiderApplicationTab = (typeof RIDER_APPLICATION_TABS)[number]['value'];

export interface RiderApplicationIdentity {
  id: string;
  user_id: string;
  full_name: string | null;
  status: string;
}

export interface RiderDocumentVersion {
  id: string;
  storage_path: string;
  created_at: string;
  is_current: boolean;
  review_status: string;
  document_kind?: string;
  superseded_at?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_reason?: string | null;
}

export interface RiderReviewState {
  adminId: string;
  selectedApplicationId: string | null;
  applicationNotes: Record<string, string>;
  applicationFeedback: Record<string, RiderApplicationFeedback>;
  sectionFeedback: RiderApplicationSectionFeedback | null;
  documentNotes: Record<string, string>;
  openedDocuments: Record<string, true>;
  failedPreviews: Record<string, true>;
  unconfirmedDocuments: Record<string, true>;
}

export interface RiderApplicationFeedback {
  kind: 'success' | 'error';
  message: string;
  requiresRefresh?: boolean;
  submittedDecision?: 'approved' | 'rejected';
}

export interface RiderApplicationSectionFeedback {
  message: string;
  applicationId: string;
  destinationTab: 'approved' | 'rejected';
  riderName: string;
}

export const RIDER_APPROVAL_READBACK_MESSAGE =
  'Approval was submitted, but the updated status could not be confirmed. Refresh the status before trying again.';

export class RiderApplicationReadbackError extends Error {
  decision: 'approved' | 'rejected';

  constructor(decision: 'approved' | 'rejected') {
    super(decision === 'approved'
      ? RIDER_APPROVAL_READBACK_MESSAGE
      : 'The application decision was submitted, but the updated status could not be confirmed. Refresh the status before trying again.');
    this.name = 'RiderApplicationReadbackError';
    this.decision = decision;
  }
}

export function riderApplicationReviewError(error: unknown) {
  const message = error instanceof Error
    ? error.message
    : error && typeof error === 'object' && 'message' in error
      ? String(error.message)
      : String(error ?? 'The server rejected the rider application decision.');
  if (error instanceof RiderApplicationReadbackError) return message;
  if (/failed to fetch|network|load failed|connection/i.test(message)) {
    return 'The rider application decision could not reach the server. Check the connection and try again.';
  }
  if (/admin required|permission denied|42501|not authorized/i.test(message)) {
    return 'Your admin session is not authorized to review rider applications. Sign in again or contact an administrator.';
  }
  if (/identity and vehicle evidence|required|document/i.test(message)) {
    return `Approval prerequisites were not satisfied: ${message}`;
  }
  if (/detailed rider decision reason|reason/i.test(message)) {
    return 'Enter a specific application decision note of at least 10 characters.';
  }
  if (/not found/i.test(message)) {
    return 'The rider application no longer exists. Refresh the status before trying again.';
  }
  return message;
}

export function parseAdminTab(value: string | null): AdminTab {
  return ADMIN_TABS.includes(value as AdminTab) ? value as AdminTab : 'orders';
}

export function parseRiderApplicationTab(value: string | null): RiderApplicationTab {
  return RIDER_APPLICATION_TABS.some((tab) => tab.value === value) ? value as RiderApplicationTab : 'pending';
}

export function riderApplicationTabForStatus(status: string): RiderApplicationTab | null {
  if (status === 'pending' || status === 'approved' || status === 'rejected') return status;
  return status === 'suspended' ? 'rejected' : null;
}

export function riderApplicationNameLabels(rows: RiderApplicationIdentity[]) {
  const names = rows.map((row) => row.full_name?.trim() ?? '');
  const counts = names.reduce<Record<string, number>>((result, name) => {
    if (name) result[name.toLocaleLowerCase()] = (result[name.toLocaleLowerCase()] ?? 0) + 1;
    return result;
  }, {});
  return Object.fromEntries(rows.map((row, index) => {
    const name = names[index];
    const shortId = row.user_id.slice(0, 8);
    return [row.id, name
      ? `${name}${(counts[name.toLocaleLowerCase()] ?? 0) > 1 ? ` · ${shortId}` : ''}`
      : `Unnamed rider · ${shortId}`];
  }));
}

export function createRiderReviewState(adminId: string): RiderReviewState {
  return { adminId, selectedApplicationId: null, applicationNotes: {}, applicationFeedback: {}, sectionFeedback: null, documentNotes: {}, openedDocuments: {}, failedPreviews: {}, unconfirmedDocuments: {} };
}

export function riderDocumentVersionKey(adminId: string, document: RiderDocumentVersion): string {
  return [adminId, document.id, document.storage_path, document.created_at].map(encodeURIComponent).join(':');
}

export function isPendingProfilePhotoReplacement(document: RiderDocumentVersion) {
  return document.document_kind === 'profile_photo' && document.is_current === false
    && document.review_status === 'pending' && document.superseded_at == null;
}

export function unambiguousPhotoReplacements<T extends RiderDocumentVersion & { application_id: string }>(documents: T[]): T[] {
  const candidates = documents.filter(isPendingProfilePhotoReplacement);
  return candidates.filter(document => candidates.filter(other => other.application_id === document.application_id).length === 1);
}

export function isVisibleRiderDocument(document: RiderDocumentVersion) {
  return document.is_current || isPendingProfilePhotoReplacement(document);
}

export function riderDocumentDecisionMatches(before: RiderDocumentVersion, after: RiderDocumentVersion, approve: boolean) {
  return riderDocumentVersionKey('', before) === riderDocumentVersionKey('', after)
    && before.document_kind === after.document_kind
    && after.review_status === (approve ? 'approved' : 'rejected')
    && after.is_current === (isPendingProfilePhotoReplacement(before) ? approve : before.is_current);
}

export function documentApprovalPrerequisite(document: RiderDocumentVersion, opened: boolean, note: string, context: { permitted?: boolean; busy?: boolean; unavailable?: boolean; previewFailed?: boolean; unconfirmed?: boolean } = {}): string | null {
  if (context.permitted === false) return 'You do not have permission to approve this document.';
  if (context.busy) return 'Waiting for the current review request to finish.';
  if (context.unavailable) return 'Current document version is not available. Refresh documents before reviewing.';
  if (context.unconfirmed) return 'The previous document decision is unconfirmed. Refresh document status before retrying.';
  if (!isVisibleRiderDocument(document)) return 'A newer file replaced this document. Open the current file before reviewing it.';
  if (document.review_status !== 'pending') return `This file is already ${document.review_status}.`;
  if (context.previewFailed) return 'Document preview failed. Reopen and successfully load the current file first.';
  if (!document.id || !document.storage_path || !document.created_at) return 'Current document version is not available.';
  if (!opened) return 'Open this exact current file and wait for the preview to load before approving or rejecting it.';
  if (note.trim().length > 2000) return 'Keep the document-specific review note within 2000 characters.';
  if (note.trim().length < 10) return 'Enter a document-specific review note of at least 10 characters.';
  return null;
}

export const RIDER_REVIEW_TIMEOUT_MS = 15_000;
export class RiderDocumentReadbackError extends Error {
  constructor() { super('The document decision is unconfirmed. Refresh document status before retrying; no decision was automatically repeated.'); }
}
export function boundedRiderReview<T>(operation: () => PromiseLike<T>, timeoutMs = RIDER_REVIEW_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new RiderDocumentReadbackError()), timeoutMs);
    Promise.resolve().then(operation).then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
  });
}
export function applicationApprovalPrerequisite(input: {
  permitted: boolean; busy: boolean; documentsAvailable: boolean; documentsApproved: boolean;
  capacityAvailable: boolean; capacityReviewed: boolean; note: string; unconfirmed: boolean;
}) {
  if (!input.permitted) return 'You do not have permission to approve this rider application.';
  if (input.busy) return 'Waiting for the current review request to finish.';
  if (input.unconfirmed) return 'The previous application decision is unconfirmed. Refresh status before retrying.';
  if (!input.documentsAvailable) return 'Refresh the current document statuses before approval.';
  if (!input.documentsApproved) return 'Approve the current identity and vehicle evidence first.';
  if (!input.capacityAvailable) return 'Vehicle and capacity review status is unavailable. Refresh before approval.';
  if (!input.capacityReviewed) return 'Review and approve the initial vehicle and capacity declaration before rider approval.';
  if (input.note.trim().length > 2000) return 'Keep the application decision note within 2000 characters.';
  if (input.note.trim().length < 10) return 'Enter at least 10 characters in the application decision note.';
  return null;
}
